import { useTeamsStore } from "@/store/teamsStore";
import { Users as UsersIcon } from "lucide-react";
import { formatUserName } from "@forgepoint/shared";

/**
 * Displays a list of user names with a users icon for multi-user custom fields.
 * @param userIds - The user IDs to resolve
 * @param inline - Whether to render in the large inline style or compact standalone style
 */
export function MultiUserFieldDisplay({
  userIds,
  inline,
}: {
  userIds: string[];
  inline: boolean;
}) {
  const users = useTeamsStore((state) => state.users);
  const names = userIds
    .map((id) => users.find((u) => u.id === id))
    .filter((u): u is NonNullable<typeof u> => u !== undefined)
    .map((u) => formatUserName(u.firstName, u.lastName, u.email));

  if (names.length === 0) {
    const emptyClass = inline
      ? "text-2xl font-bold text-muted-foreground/30"
      : "text-sm text-muted-foreground/50";
    return <p className={emptyClass}>&mdash;</p>;
  }

  if (inline) {
    return (
      <div className="flex items-center gap-2">
        <UsersIcon className="h-5 w-5 text-muted-foreground shrink-0" />
        <p className="text-2xl font-bold">{names.join(", ")}</p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <UsersIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <p className="text-sm font-medium">{names.join(", ")}</p>
    </div>
  );
}
